import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  useGetAnalysisWidgetAnalyticsConfig,
  useGetWidgetAnalysisConfig
} from "@/api/widget/useWidgetAPI";
import { buildSeriesFromData, ChartSeries } from "@/components/ui/chart/utils";
import { GROUPED_CHART_COLORS } from "@/constants/colors";
import { RootState } from "@/store";
import { AggregationType, WidgetConfig, WidgetType } from "@/types/widget";
import {
  buildAnalysisAreaFilter,
  getColumnDisplayName,
  mapPostAnalysisWidgetAnalyticsPayload,
  mapPostWidgetAnalysisConfigPayload
} from "@/utils/widget";

export default function useDatasetWidgetSummary(
  widget: WidgetConfig,
  options?: { enabled?: boolean; analysisId?: string }
) {
  const isEnabled = options?.enabled ?? true;
  const analysisId = options?.analysisId;

  const { filters, datasets, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );
  const { gridsSelected } = useSelector(
    (state: RootState) => state.analysisSummary
  );

  const lastIndex = widget.widget_id.lastIndexOf("_");
  const widgetId = widget.widget_id.slice(0, lastIndex);

  const datasetEntry = datasets[widget.dataset_id];

  const analysisAreaFilter = useMemo(
    () =>
      analysisId
        ? buildAnalysisAreaFilter(gridsSelected, rawDatasets, widget.dataset_id)
        : [],
    [analysisId, gridsSelected, rawDatasets, widget.dataset_id]
  );

  // Both hooks are always called (React rules). Only one fetches at a time —
  // regular is disabled when analysisId is present, analysis is disabled when absent.
  const { data: regularResult, isLoading: isRegularResultLoading } =
    useGetWidgetAnalysisConfig(
      mapPostWidgetAnalysisConfigPayload(
        widget,
        filters[widgetId] ?? [],
        datasetEntry?.h3Configuration,
        datasetEntry?.group_by
      ),
      { enabled: isEnabled && !analysisId }
    );
  const {
    data: analysisResult,
    isLoading: isAnalysisResultLoading,
    isError: isAnalysisError,
    error: rawAnalysisError,
    refetch: refetchAnalysisWidget
  } = useGetAnalysisWidgetAnalyticsConfig(
    mapPostAnalysisWidgetAnalyticsPayload(
      widget,
      analysisId ?? "",
      analysisAreaFilter
    ),
    { enabled: isEnabled && !!analysisId }
  );

  const widgetAnalysisResult = analysisId ? analysisResult : regularResult;

  // Analysis widgets come from published results, so invalidWidget never applies.
  const invalidWidget = !analysisId && !datasetEntry?.data?.published;

  const isStructureChange = useMemo(() => {
    const schema = rawDatasets[widget.dataset_id]?.columns ?? [];
    const schemaCodes = new Set(schema.map((s) => s.code));
    const breakdownGroupIds = new Set(
      schema
        .filter((s) => s.breakdown_group_id)
        .map((s) => s.breakdown_group_id!)
    );

    const dimensionMissing =
      !!widget.dimension && !schemaCodes.has(widget.dimension);
    const measureMissing =
      !!widget.measure &&
      !schemaCodes.has(widget.measure) &&
      !breakdownGroupIds.has(widget.measure);

    return dimensionMissing || measureMissing;
  }, [rawDatasets, widget.dataset_id, widget.dimension, widget.measure]);

  const rawData = useMemo(() => {
    return (
      widgetAnalysisResult?.data?.data?.slice(
        0,
        widget.configuration.dimension_values_shown
      ) || []
    );
  }, [widgetAnalysisResult, widget]);

  const groupedData = useMemo(() => {
    const rows = widgetAnalysisResult?.data?.rows;
    const dimension = widgetAnalysisResult?.data?.dimension;

    if (!rows?.length || !dimension) return null;

    const series = buildSeriesFromData(rows, dimension);
    if (series.length <= 1) return null;

    return {
      rows: rows.slice(0, widget.configuration.dimension_values_shown),
      nameKey: dimension,
      series
    };
  }, [widgetAnalysisResult, widget]);

  const bigNumberData = useMemo((): {
    type: AggregationType;
    value: number;
    measure: string;
  } | null => {
    if (widget.chart_type !== WidgetType.BIG_NUMBER) return null;
    if (
      widgetAnalysisResult?.data?.value !== undefined &&
      widgetAnalysisResult?.data?.value !== null
    ) {
      return {
        type:
          (widgetAnalysisResult.data.aggregation as AggregationType) ??
          AggregationType.COUNT,
        value: widgetAnalysisResult.data.value,
        measure: widgetAnalysisResult.data.measure || ""
      };
    }
    return null;
  }, [widgetAnalysisResult, widget]);

  const analysisError =
    isAnalysisError && rawAnalysisError?.code !== 400 ? rawAnalysisError : null;

  const isChartLoading = isRegularResultLoading || isAnalysisResultLoading;

  const lineSeries = useMemo((): ChartSeries[] | undefined => {
    if (!widget.measure) return undefined;
    const label = getColumnDisplayName(
      rawDatasets,
      widget.dataset_id,
      widget.measure
    );
    return [{ dataKey: "value", color: GROUPED_CHART_COLORS[0], label }];
  }, [rawDatasets, widget.dataset_id, widget.measure]);

  return {
    invalidWidget,
    isStructureChange,
    rawData,
    bigNumberData,
    groupedData,
    analysisError,
    refetchAnalysisWidget,
    isChartLoading,
    lineSeries
  };
}
